import React, { useEffect, useRef, useState } from 'react'

import { FormattedMessage } from 'react-intl'

import { useIntl } from '@context/Internationalization/useIntl'
import Theme from '@theme'
import { Icon } from '@tribegroup'
import {
  CountGroup,
  ResultCountRow,
  SortCheck,
  SortDropdown,
  SortDropdownItem,
  SortDropdownMenu,
  SortTrigger,
} from './ResultCount.styled'

interface IProps {
  visible: number
  total: number
  sortField: string
  sortOptions: string[]
  onSortFieldChange: (next: string) => void
  // Optional control rendered beside the result count (e.g. enter-selection).
  action?: React.ReactNode
  // Optional test id for the row. Kept caller-supplied so this shared component
  // (also used by ShortlistCandidatesView) carries no view-specific name.
  rowTestId?: string
}

const ResultCount: React.FC<IProps> = ({
  visible,
  total,
  sortField,
  sortOptions,
  onSortFieldChange,
  action,
  rowTestId,
}) => {
  const { intl } = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return undefined
    const handler = (e: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const labelFor = (field: string): string =>
    intl.formatMessage({
      id: `creatorSearch.vibeCheck.list.sortField.${field}`,
      defaultMessage: field,
    })

  return (
    <ResultCountRow data-testid={rowTestId}>
      <CountGroup>
        <span>
          <FormattedMessage
            id="creatorSearch.vibeCheck.list.resultCount"
            values={{ visible, total }}
          />
        </span>
        {action}
      </CountGroup>
      <SortDropdown ref={dropdownRef}>
        <SortTrigger
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="sort-field-toggle"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <FormattedMessage
            id="creatorSearch.vibeCheck.list.sortByPrefix"
            values={{ field: labelFor(sortField).toUpperCase() }}
          />
          <Icon glyph="sort" size={1} color={Theme.grey900Color} />
        </SortTrigger>
        {menuOpen && (
          <SortDropdownMenu role="menu">
            {sortOptions.map((opt) => {
              const isSelected = opt === sortField
              return (
                <SortDropdownItem
                  key={opt}
                  role="menuitem"
                  onClick={() => {
                    onSortFieldChange(opt)
                    setMenuOpen(false)
                  }}
                  data-testid={`sort-option-${opt}`}
                >
                  <span>{labelFor(opt)}</span>
                  {isSelected && (
                    <SortCheck>
                      <Icon glyph="tick-heavy" size={1} color={Theme.primaryColor} />
                    </SortCheck>
                  )}
                </SortDropdownItem>
              )
            })}
          </SortDropdownMenu>
        )}
      </SortDropdown>
    </ResultCountRow>
  )
}

export { ResultCount }
export default ResultCount
